import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function FollowButton({ pseudonym }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // { following, follower_count, is_self }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      if (user && user !== false) {
        const { data } = await api.get(`/authors/${encodeURIComponent(pseudonym)}/follow-status`);
        setStatus(data);
      } else {
        const { data } = await api.get(`/authors/${encodeURIComponent(pseudonym)}/followers/count`);
        setStatus({ following: false, follower_count: data.count, is_self: false, anonymous: true });
      }
    } catch (err) {
      console.error("Follow-status load failed", err);
    }
  };

  useEffect(() => { load(); }, [pseudonym, user]);

  const toggle = async () => {
    if (!user || user === false) {
      toast.info("Sign in to follow this pen name.");
      return;
    }
    setBusy(true);
    try {
      if (status?.following) {
        await api.delete(`/authors/${encodeURIComponent(pseudonym)}/follow`);
        setStatus((s) => ({ ...s, following: false, follower_count: Math.max(0, (s?.follower_count || 1) - 1) }));
        toast.success(`Unfollowed ${pseudonym}.`);
      } else {
        await api.post(`/authors/${encodeURIComponent(pseudonym)}/follow`);
        setStatus((s) => ({ ...s, following: true, follower_count: (s?.follower_count || 0) + 1 }));
        toast.success(`Following ${pseudonym}. We'll email you when they publish.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (status?.is_self) {
    return (
      <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass" data-testid="follow-self">
        {status.follower_count} {status.follower_count === 1 ? "reader follows you" : "readers follow you"}
      </div>
    );
  }

  const following = status?.following;
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm font-mono-brass text-[10px] uppercase tracking-widest font-semibold transition-colors duration-200 ${
        following ? "btn-ghost-gold" : "btn-buy"
      } disabled:opacity-50`}
      data-testid="follow-toggle"
    >
      <Heart className={`w-3.5 h-3.5 ${following ? "fill-[#D9B472] text-[#D9B472]" : ""}`} />
      {busy ? "…" : following ? "Following" : "Follow"}
      {status && (
        <span className="ml-1 text-[#6E675E]" data-testid="follower-count">· {status.follower_count}</span>
      )}
    </button>
  );
}
